/**
 * The one popover panel the header uses (task 201): the More menu, the
 * account menu and the Client listbox all sit on it, so they dismiss,
 * focus and look the same way.
 *
 *   - Click outside or Escape closes it, and Escape puts focus back on
 *     the button that opened it.
 *   - Arrow keys walk the items; Home and End jump; Tab leaves and closes.
 *   - It is a white card on the navy canvas, the same inversion as every
 *     panel in the product.
 *
 * It is not a modal: nothing is scrimmed, the page behind stays live.
 * The owner renders the trigger and passes its ref, so the panel can
 * position under it (`align`) and return focus to it.
 */

import { useEffect, useRef, type ReactNode, type RefObject } from 'react';

const ITEMS = '[role="menuitem"],[role="option"],input:not([disabled])';

export function MenuPanel({
  open,
  onClose,
  triggerRef,
  align = 'left',
  role = 'menu',
  label,
  className = '',
  children,
}: {
  open: boolean;
  onClose: () => void;
  triggerRef: RefObject<HTMLElement | null>;
  align?: 'left' | 'right';
  role?: 'menu' | 'listbox';
  label: string;
  className?: string;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointer = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        triggerRef.current?.focus();
        return;
      }
      const panel = panelRef.current;
      if (!panel || !panel.contains(document.activeElement)) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(ITEMS));
      if (items.length === 0) return;
      const i = items.indexOf(document.activeElement as HTMLElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        items[(i + 1) % items.length]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        items[(i - 1 + items.length) % items.length]?.focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        items[0]?.focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        items[items.length - 1]?.focus();
      } else if (e.key === 'Tab') {
        onClose();
      }
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, triggerRef]);

  // Focus lands on the first item (or the find box) when the panel opens.
  useEffect(() => {
    if (!open) return;
    const first = panelRef.current?.querySelector<HTMLElement>(ITEMS);
    first?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      role={role}
      aria-label={label}
      className={
        'absolute top-full z-50 mt-2 min-w-56 rounded-cardInner bg-white p-1.5 text-bluesphere-textOnLight ' +
        'shadow-modal ring-1 ring-black/10 ' +
        (align === 'right' ? 'right-0 ' : 'left-0 ') +
        className
      }
    >
      {children}
    </div>
  );
}

/** One row in a `MenuPanel`: the shared hover, focus and padding. */
export const menuItemClass =
  'flex w-full items-center justify-between gap-3 rounded-[10px] px-3 py-2 text-left text-sm ' +
  'text-bluesphere-textOnLight transition-colors hover:bg-bluesphere-surfaceAlt ' +
  'focus-visible:outline-hidden focus-visible:bg-bluesphere-surfaceAlt';

export const menuItemCurrentClass = 'bg-bluesphere-surfaceAlt font-semibold';

/** A greyed, unreachable row: a section that exists on the roadmap, not yet in the product. */
export function SoonItem({ label }: { label: string }) {
  return (
    <span
      aria-disabled="true"
      className={`${menuItemClass} cursor-not-allowed text-bluesphere-muted`}
    >
      {label}
      <span className="text-[10px] uppercase tracking-wider">soon</span>
    </span>
  );
}

export function MenuDivider() {
  return <hr className="mx-1 my-1.5 border-0 border-t border-bluesphere-border" />;
}

/** The brand-blue dot that marks the current item. */
export function CurrentDot() {
  return (
    <span
      aria-hidden="true"
      className="h-1.5 w-1.5 shrink-0 rounded-full bg-bluesphere-secondary"
    />
  );
}
